import { useEffect, useState } from 'react';
import { useSubmissions } from '@/hooks/useSubmissions';
import type { EPIRequest } from '@/types';

const statusColors: Record<string, string> = {
  Pendente: 'bg-amber-100 text-amber-700',
  Aprovado: 'bg-emerald-100 text-emerald-700',
  Negado: 'bg-red-100 text-red-700',
};

export default function AdminSolicitacoesEPI() {
  const { epiRequests, counts, updateEPIStatus } = useSubmissions();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('Todos');
  const [selected, setSelected] = useState<EPIRequest | null>(null);
  const [statusDraft, setStatusDraft] = useState<EPIRequest['status']>('Pendente');
  const [reasonDraft, setReasonDraft] = useState('');

  useEffect(() => {
    if (selected) {
      setStatusDraft(selected.status);
      setReasonDraft(selected.statusReason || '');
    }
  }, [selected]);

  const filtered = epiRequests.filter((r) => {
    const matchesSearch =
      (r.displayId || r.id).toLowerCase().includes(search.toLowerCase()) ||
      r.userName.toLowerCase().includes(search.toLowerCase()) ||
      r.setor.toLowerCase().includes(search.toLowerCase()) ||
      r.motivo.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'Todos' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-portal-gray-900">Solicitações de EPI</h1>
        <p className="text-sm text-portal-gray-500">
          Gerencie todas as solicitações de equipamentos de proteção individual.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total', value: counts.epi, color: 'bg-portal-purple text-white' },
          { label: 'Pendentes', value: counts.epiPendentes, color: 'bg-amber-100 text-amber-700' },
          { label: 'Aprovadas', value: counts.epiAprovadas, color: 'bg-emerald-100 text-emerald-700' },
          { label: 'Negadas', value: counts.epiNegadas, color: 'bg-red-100 text-red-700' },
        ].map((s) => (
          <div key={s.label} className={`${s.color} rounded-xl p-4`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium opacity-90">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-portal-gray-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, setor ou motivo..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-portal-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-portal-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer"
          >
            <option>Todos</option>
            <option>Pendente</option>
            <option>Aprovado</option>
            <option>Negado</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-portal-gray-100 flex items-center justify-between">
          <span className="text-sm font-medium text-portal-gray-700">
            Solicitações ({filtered.length})
          </span>
        </div>
        {filtered.length === 0 ?(
          <div className="p-12 text-center">
            <i className="ri-inbox-line text-portal-gray-300 text-3xl mb-3 block"></i>
            <p className="text-sm text-portal-gray-500">Nenhuma solicitação encontrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-portal-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-portal-gray-600">ID</th>
                  <th className="text-left px-4 py-3 font-medium text-portal-gray-600">Colaborador</th>
                  <th className="text-left px-4 py-3 font-medium text-portal-gray-600">Setor</th>
                  <th className="text-left px-4 py-3 font-medium text-portal-gray-600">Motivo</th>
                  <th className="text-left px-4 py-3 font-medium text-portal-gray-600">Urgência</th>
                  <th className="text-center px-4 py-3 font-medium text-portal-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-portal-gray-600">Data</th>
                  <th className="text-center px-4 py-3 font-medium text-portal-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-portal-gray-100">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-portal-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-portal-gray-500 font-mono text-xs">{r.displayId || r.id}</td>
                    <td className="px-4 py-3 text-portal-gray-800 font-medium">{r.userName}</td>
                    <td className="px-4 py-3 text-portal-gray-600">{r.setor}</td>
                    <td className="px-4 py-3 text-portal-gray-600 max-w-xs truncate">{r.motivo}</td>
                    <td className="px-4 py-3 text-portal-gray-600">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        r.urgencia.includes('Imediato') ?'bg-red-100 text-red-700' :
                        r.urgencia.includes('Médio') ?'bg-amber-100 text-amber-700' :
                        'bg-portal-purple-100 text-portal-purple'
                      }`}>
                        {r.urgencia}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusColors[r.status] || 'bg-portal-gray-100 text-portal-gray-600'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-portal-gray-500 text-xs">
                      {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setSelected(r)}
                        className="text-portal-purple hover:text-portal-purple-dark text-xs font-medium cursor-pointer"
                      >
                        Ver detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)}></div>
          <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-portal-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-portal-gray-800">Detalhes da Solicitação</h3>
              <button
                onClick={() => setSelected(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-portal-gray-400 hover:text-portal-gray-600 hover:bg-portal-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">ID</p>
                  <p className="text-sm font-mono text-portal-gray-700">{selected.displayId || selected.id}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Colaborador</p>
                  <p className="text-sm font-medium text-portal-gray-800">{selected.userName}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Setor</p>
                  <p className="text-sm font-medium text-portal-gray-800">{selected.setor}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Motivo</p>
                  <p className="text-sm text-portal-gray-700">{selected.motivo}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Urgência</p>
                  <p className="text-sm text-portal-gray-700">{selected.urgencia}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Status</p>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[selected.status]}`}>
                    {selected.status}
                  </span>
                </div>
              </div>
              <div className="rounded-xl border border-portal-gray-200 p-4 space-y-3">
                <div>
                  <label className="text-xs text-portal-gray-500 mb-1 block">Alterar status</label>
                  <select
                    value={statusDraft}
                    onChange={(e) => setStatusDraft(e.target.value as EPIRequest['status'])}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20"
                  >
                    <option value="Pendente">Pendente</option>
                    <option value="Aprovado">Aprovado</option>
                    <option value="Negado">Negado</option>
                  </select>
                </div>
                {statusDraft === 'Negado' && (
                  <textarea
                    value={reasonDraft}
                    onChange={(e) => setReasonDraft(e.target.value)}
                    placeholder="Justifique o motivo da negativa"
                    className="w-full min-h-24 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20"
                  />
                )}
                <button
                  type="button"
                  onClick={async () => {
                    if (statusDraft === 'Negado' && !reasonDraft.trim()) return;
                    try {
                      await updateEPIStatus(selected.id, statusDraft, reasonDraft.trim());
                      setSelected(null);
                    } catch (err) {
                      alert(err instanceof Error ? err.message : 'Não foi possível salvar o status.');
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-portal-purple rounded-lg hover:bg-portal-purple-dark disabled:opacity-50"
                  disabled={statusDraft === 'Negado' && !reasonDraft.trim()}
                >
                  Salvar status
                </button>
              </div>
              {selected.observacao && (
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Observação</p>
                  <p className="text-sm text-portal-gray-700 bg-portal-gray-50 rounded-lg p-3">{selected.observacao}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-portal-gray-400 mb-2">Itens Solicitados</p>
                <div className="border border-portal-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-portal-gray-50">
                        <th className="text-left px-3 py-2 font-medium text-portal-gray-600">Item</th>
                        <th className="text-center px-3 py-2 font-medium text-portal-gray-600">Qtd 01</th>
                        <th className="text-center px-3 py-2 font-medium text-portal-gray-600">Qtd 02</th>
                        <th className="text-center px-3 py-2 font-medium text-portal-gray-600">Caixa</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-portal-gray-100">
                      {selected.itens.map((item, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-portal-gray-700">{item.tipo}</td>
                          <td className="px-3 py-2 text-center text-portal-gray-600">{item.q01}</td>
                          <td className="px-3 py-2 text-center text-portal-gray-600">{item.q02}</td>
                          <td className="px-3 py-2 text-center text-portal-gray-600">{item.qCaixa}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-portal-gray-100 flex justify-end">
              <button
                onClick={() => setSelected(null)}
                className="px-4 py-2 text-sm font-medium text-portal-gray-600 bg-white border border-portal-gray-200 rounded-lg hover:bg-portal-gray-50 cursor-pointer whitespace-nowrap"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
